import { FC, useMemo } from "react";
import Grid from "elements/Grid";
import TextField from "elements/Input/TextField";
import { useRouter } from "next/navigation";
import MagnifyingGlassIcon from "public/landing/magnifying_glass_rounded.svg";
import { useEffect, useState } from "react";
import Breadcrumbs from "./Breadcrumbs";
import Fuse from "fuse.js";
import { useCallback } from "react";
import { IArticle, HelpCenterBreadcrumbs } from "utils/types";
import { sendWebhookToHelpCenter } from "utils/sendWebhook";
import { formatHelpCenterUrl } from "./helpCenterUrlUtils";
import { useEventTracker_DEPRECATED } from "utils/useEventTracker";
import constants from "utils/constants";
import { useAppContext } from "context/AppContext";

interface SearchHeaderProps {
	header: string;
	breadcrumbs?: HelpCenterBreadcrumbs[];
	articles: IArticle[];
	noResultsCounter?: number;
}

const SEARCH_RESULTS_MAX_AMOUNT = 4;
const SEARCH_RESULTS_MIN_CHAR = 3;
const SEARCH_RESULTS_MAX_BODY_CHAR = 120;

const DISABLE_AUTO_SUGGESTIONS = true;

const SearchHeader: FC<SearchHeaderProps> = ({
	header,
	breadcrumbs,
	articles,
	noResultsCounter = 0,
}) => {
	const router = useRouter();
	const [search, setSearch] = useState("");
	const [searchResults, setSearchResults] = useState<IArticle[]>([]);
	const [activeIndex, setActiveIndex] = useState<number | null>(null);
	const [showResults, setShowResults] = useState(false);
	const [showNoResultsBanner, setShowNoResultsBanner] = useState(false);
	const { mixpanelInitialized } = useAppContext();

	const trackHelpCenterSearch = useEventTracker_DEPRECATED(
		constants.EVENTS.HELP_CENTER_SEARCHED,
		{
			"Search Query": search,
		}
	);

	const routeSearchResults = (input?: string) => {
		if (mixpanelInitialized && search.length > 0) {
			trackHelpCenterSearch();
		}
		if (input) {
			sendWebhookToHelpCenter("Sent to Article - Searched: " + search);
			router.push("/help/article/" + encodeURIComponent(input));
		} else if (search.length > 0) {
			sendWebhookToHelpCenter("Sent to Search Results - Searched: " + search);
			router.push("/help/results" + "?search=" + encodeURIComponent(search));
		}
	};

	const magnifyingGlassClicked = () => {
		if (activeIndex !== null && searchResults) {
			routeSearchResults(
				formatHelpCenterUrl(
					searchResults[activeIndex].id,
					searchResults[activeIndex].title
				)
			);
		} else {
			routeSearchResults();
		}
	};

	//keep this in sync with backend /help/search
	const fuse = useMemo(
		() =>
			new Fuse(articles, {
				keys: ["title", "body"],
				threshold: 0,
				ignoreLocation: true,
				useExtendedSearch: true,
			}),
		[articles]
	);

	useEffect(() => {
		if (search) {
			const adaptedSearchTerm = search
				.trim()
				.split(" ")
				.reduce(
					(previousValue, currentValue) => previousValue + ` '${currentValue}`,
					""
				);
			const results = fuse.search(adaptedSearchTerm);
			setSearchResults(
				results.map(val => val.item).slice(0, SEARCH_RESULTS_MAX_AMOUNT)
			);
		} else {
			setSearchResults([]);
		}
	}, [search, fuse]);

	const handleKeyPress = useCallback(
		e => {
			if (!searchResults || !search) return;

			if (DISABLE_AUTO_SUGGESTIONS) {
				if (e.key === "Enter") {
					routeSearchResults();
				}
				return;
			}

			if (e.key === "ArrowUp") {
				e.preventDefault();
				let newIndex;
				if (activeIndex !== null) {
					newIndex =
						activeIndex === 0 ? searchResults.length - 1 : activeIndex - 1;
				} else {
					newIndex = searchResults.length - 1;
				}
				setActiveIndex(newIndex);
			}

			if (e.key === "ArrowDown") {
				e.preventDefault();
				let newIndex;
				if (activeIndex !== null) {
					newIndex =
						activeIndex === searchResults.length - 1 ? 0 : activeIndex + 1;
				} else {
					newIndex = 0;
				}
				setActiveIndex(newIndex);
			}
			if (e.key === "Enter") {
				e.preventDefault();
				if (activeIndex === null) {
					routeSearchResults();
				} else {
					routeSearchResults(
						formatHelpCenterUrl(
							searchResults[activeIndex].id,
							searchResults[activeIndex].title
						)
					);
				}
			}
		},
		[activeIndex, search]
	);

	useEffect(() => {
		if (!showResults || (showResults && search)) {
			setActiveIndex(null);
		}
	}, [showResults, search]);

	useEffect(() => {
		const handler = () => {
			setShowResults(false);
			setShowNoResultsBanner(false);
		};

		window.addEventListener("click", handler);

		return () => {
			window.removeEventListener("click", handler);
		};
	}, []);

	useEffect(() => {
		if (search.length >= SEARCH_RESULTS_MIN_CHAR && searchResults?.length) {
			setShowResults(true);
		} else if (!searchResults?.length) {
			setShowResults(false);
		}
	}, [searchResults, search]);

	useEffect(() => {
		if (search.length > 0 && !showResults) {
			setShowNoResultsBanner(true);
		} else {
			setShowNoResultsBanner(false);
		}
	}, [search, showResults]);

	const findSearchLocation = (body: string, search: string) => {
		return body.toLowerCase().indexOf(search.toLowerCase());
	};

	const searchBody = (body: string) => {
		if (!search) return;
		let searchEl = search;
		//if word is not found and there is more than 1 word, then only search by first word
		if (
			findSearchLocation(body, searchEl) === -1 &&
			search.split(" ").length > 1
		) {
			searchEl = search.split(" ")[0];
		}
		const searchTermLocation = findSearchLocation(body, searchEl);
		if (searchTermLocation >= 0) {
			return (
				(searchTermLocation > 0 ? "..." : "") +
				body.slice(
					searchTermLocation,
					SEARCH_RESULTS_MAX_BODY_CHAR + searchTermLocation
				) +
				(searchTermLocation > 0 &&
				SEARCH_RESULTS_MAX_BODY_CHAR + searchTermLocation + searchEl.length <
					body.length
					? "..."
					: "")
			);
		} else {
			return body;
		}
	};

	const noResultsBannerClicked = () => {
		sendWebhookToHelpCenter(
			"Sent to contact form after 2nd no results: " + search
		);
		router.push("/help/new-request");
	};

	const checkMyStatusRecommended = () => {
		sendWebhookToHelpCenter(
			"Search - Recommended 'check status'. User typed: " + search
		);
		router.push("/help/results?search=check%20status");
	};

	const searchIncludesNumbers = useMemo(() => {
		return /\d/.test(search);
	}, [search]);

	return (
		<div className="help-center-search-container">
			<Grid className="help-center-search-grid">
				<div className="help-center-search">
					<Breadcrumbs breadcrumbs={breadcrumbs} />
					<h3>{header}</h3>
					<div className="help-center-search-input">
						<TextField
							placeholder="Search our help center..."
							onChange={e => {
								setSearch(e.target.value);
							}}
							onKeyDown={handleKeyPress}
							className={showResults ? "help-center-search-input-active" : ""}
						/>
						<MagnifyingGlassIcon
							className="help-center-search-icon-right"
							onClick={magnifyingGlassClicked}
						/>
						{!DISABLE_AUTO_SUGGESTIONS &&
							showResults &&
							searchResults.length > 0 && (
								<div className="help-center-search-autocomplete">
									{searchResults.map((val, index) => {
										return (
											<div
												key={val.title}
												onClick={() =>
													routeSearchResults(
														formatHelpCenterUrl(val.id, val.title)
													)
												}>
												<div
													className={`help-center-search-autocomplete-result ${
														index === activeIndex
															? "help-center-search-autocomplete-result-active"
															: ""
													}`}
													key={val.title}>
													<div>
														<p className="help-center-search-autocomplete-result-title">
															{val.title}
														</p>
														{!!val.body && (
															<p className="sm help-center-search-autocomplete-result-snippet">
																{searchBody(val.body)}
															</p>
														)}
													</div>
												</div>
											</div>
										);
									})}
								</div>
							)}
						{!DISABLE_AUTO_SUGGESTIONS && showNoResultsBanner && (
							<div className="help-center-search-autocomplete">
								{search.length < SEARCH_RESULTS_MIN_CHAR ? (
									<div className="help-center-search-autocomplete-no-results button-small">
										Keep typing...
									</div>
								) : searchIncludesNumbers ? (
									<div
										className="help-center-search-autocomplete-no-results button-small help-center-search-autocomplete-no-results-route-to-contact"
										onClick={() => checkMyStatusRecommended()}>
										Did you mean "
										<span className="underline">check my status</span>"?
									</div>
								) : noResultsCounter ? (
									<div
										className="help-center-search-autocomplete-no-results button-small help-center-search-autocomplete-no-results-route-to-contact"
										onClick={() => noResultsBannerClicked()}>
										No results found.{" "}
										<span className="underline">Create a new request</span>
									</div>
								) : (
									<div className="help-center-search-autocomplete-no-results button-small">
										No results found
									</div>
								)}
							</div>
						)}
					</div>
				</div>
			</Grid>
		</div>
	);
};
export default SearchHeader;
